import React from 'react';
import { GraduationCap, Target, Eye, CheckCircle, Users, Network, TrendingUp } from 'lucide-react';

const Nosotros = () => {
  const objectives = [
    'Identificar los problemas relacionados a la educación formal y no formal, y asegurar los fondos de apoyo de investigación de fuentes públicas y privadas',
    'Oportunidades de consultoría para el área educativa e investigación con los sectores público y privado',
    'Identificar oportunidades de investigación de posgrado en ciencias de la educación',
    'Iniciar y participar en el diseño y ejecución de seminarios, diplomados, estancias académicas y posdoctorados',
    'Construir una base de datos de información sobre cuestiones relacionadas con la educación',
    'Mantener la serie de publicaciones que sintetizan e informan sobre vinculaciones de la educación con el resto de las disciplinas'
  ];

  const strategicAxes = [
    'Desarrollo Humano Integral',
    'Desarrollo Sostenible',
    'Pertinencia Cultural',
    'Transformación Social',
    'Deberes y Derechos Humanos',
    'Ética y Ciudadanía',
    'Investigación Educativa',
    'Formación de Educadores y Educadoras',
    'Desarrollo Profesional de Educadores y Educadoras',
    'Educación Continua',
    'Extensión',
    'Producción Académica',
    'Tecnología Educativa',
    'Administración Académica'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <GraduationCap className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-5xl font-bold mb-2">Nosotros</h1>
              <p className="text-xl text-green-100">Centro de Estudios e Investigaciones para el Desarrollo Docente</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Main Description */}
        <section className="mb-20">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#e87729]">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              El <strong>Centro de Estudios e Investigaciones para el Desarrollo Docente CENID</strong>, es una institución 
              dedicada a la investigación interdisciplinaria, la docencia y la prestación de servicios técnicos, que tiene por 
              objeto el abordaje de problemas relacionados con la educación, ciencia, la tecnología, la innovación, las estrategias 
              de desarrollo y la educación superior en México e Iberoamérica.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              El Centro tiene sus bases de la Red Intercontinental de Investigaciones sobre Educación en Línea, RIEL, una entidad 
              sin fines de lucro fundada en el año <strong>2007</strong>, dedicada a promover estudios sobre la ciencia, la tecnología 
              y sus vínculos con la sociedad en Iberoamérica.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              En octubre del <strong>2010</strong>, con el propósito de profundizar la actividad de investigación y servicios de 
              consultoría especializada, la RIEL tomó la decisión de crear el Centro de Estudios e Investigaciones para el Desarrollo 
              Docente, CENID A.C.
            </p>
          </div>

          {/* Recognition Badge */}
          <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-start gap-4">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-800 font-medium">
                  A partir del mes de mayo del <strong>2012</strong>, el Consejo Nacional de Ciencia y Tecnología de México, 
                  SECIHTI, otorgó el registro <strong>14658</strong> a través del Registro Nacional de Instituciones y Empresas 
                  Científicas y Tecnológicas RENIECYT, para poder ejercer como centro de investigaciones reconocido.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mb-20">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-white/20 p-3 rounded-lg">
                  <Target className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold">Misión</h2>
              </div>
              <p className="text-lg leading-relaxed text-white/95">
                El Centro de Estudios e Investigaciones para el Desarrollo, es una institución dedicada a la investigación 
                interdisciplinaria, la docencia y la prestación de servicios técnicos, que tiene por objeto el abordaje de 
                problemas relacionados con la educación, ciencia, la tecnología, la innovación, las estrategias de desarrollo 
                y la educación superior en México e Iberoamérica.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-xl p-8 hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-white/30 p-3 rounded-lg">
                  <Eye className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold">Visión</h2>
              </div>
              <p className="text-lg leading-relaxed">
                Ser un centro de investigación educativa de excelencia focalizado en la gestión académica y de investigación, 
                la capacitación e investigación educativa, mediante la formación docente en posgrados y eventos de capacitación 
                continua en apoyo de las modalidades educativas no convencionales apoyándose en el trabajo colaborativo nacional 
                e internacional que logre convertirse en un punto de referencia para el desarrollo docente en Iberoamérica.
              </p>
            </div>
          </div>
        </section>

        {/* Objectives */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-[#0b742f]/5 px-6 py-3 rounded-full mb-4">
              <CheckCircle className="w-6 h-6 text-[#0b742f]" />
              <h2 className="text-3xl font-bold text-gray-900">Objetivos</h2>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {objectives.map((objective, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b742f] group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-[#0b742f]/10 group-hover:bg-[#0b742f] p-3 rounded-lg transition-colors duration-300 flex-shrink-0">
                    <span className="text-[#0b742f] group-hover:text-white font-bold text-lg transition-colors duration-300">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{objective}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Organization */}
        <section className="mb-20">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Organización</h2>
            </div>
            
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                La estructura organizativa responde a un enfoque interdisciplinario y aplicado. El Centro cuenta con un 
                <strong> Director</strong>, un <strong>Coordinador de Investigación</strong> y un <strong>Coordinador de Docencia</strong>.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Desde el punto de vista de las capacidades disciplinarias y temáticas, el Centro está organizado en 
                <strong> Áreas de investigación</strong> y de <strong>servicios científicos y tecnológicos</strong>, que definen 
                los ámbitos de interacción. Cada una de las Áreas de Trabajo posee Líneas de Investigación específicas.
              </p>

              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-start gap-3">
                  <Network className="w-6 h-6 text-[#0b742f] flex-shrink-0 mt-1" />
                  <p className="text-gray-700 leading-relaxed">
                    Las Áreas no funcionan como compartimentos estáticos, sino más bien presentan un carácter flexible que 
                    permite un intercambio fluido entre los distintos equipos de investigación.
                  </p>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                Desde el punto de vista operativo, la actividad del Centro se organiza en <strong>Programas horizontales</strong> 
                en los que participan investigadores de dos o más áreas y en los proyectos de investigación y consultoría que 
                generan un importante nivel de producción académica y profesional que se expresa en publicaciones científicas 
                y de difusión, así como en la prestación de servicios.
              </p>
            </div>
          </div>
        </section>

        {/* Strategic Axes */}
        <section>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-[#e87729]/10 px-6 py-3 rounded-full mb-4">
              <TrendingUp className="w-6 h-6 text-[#e87729]" />
              <h2 className="text-3xl font-bold text-gray-900">Ejes Estratégicos</h2>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Nuestros ejes estratégicos guían el desarrollo y la implementación de programas educativos de excelencia
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {strategicAxes.map((axis, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 border border-gray-100 hover:border-[#e87729] group text-center"
              >
                <div className="w-2 h-2 bg-[#e87729] rounded-full mx-auto mb-3 group-hover:scale-150 transition-transform duration-300"></div>
                <p className="text-sm font-medium text-gray-700 group-hover:text-[#0b742f] transition-colors duration-300">
                  {axis}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="mt-20">
          <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Nuestra Historia</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-[#0b742f] text-white font-bold rounded-lg px-4 py-2 flex-shrink-0">
                  2007
                </div>
                <p className="text-gray-700 pt-2">
                  Fundación de RIEL (Red Intercontinental de Investigaciones sobre Educación en Línea)
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-[#e87729] text-white font-bold rounded-lg px-4 py-2 flex-shrink-0">
                  2010
                </div>
                <p className="text-gray-700 pt-2">
                  Creación del Centro de Estudios e Investigaciones para el Desarrollo Docente, CENID A.C.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-[#0b742f] text-white font-bold rounded-lg px-4 py-2 flex-shrink-0">
                  2012
                </div>
                <p className="text-gray-700 pt-2">
                  SECIHTI otorga el registro 14658 a través del RENIECYT como centro de investigaciones reconocido
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Nosotros;
